import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import { Add, Edit, Delete, LibraryBooks } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { gammeApi, Gamme } from '../services/gammeApi';

const GammePage: React.FC = () => {
    const { t } = useLanguage();
    
    const [gammes, setGammes] = useState<Gamme[]>([]);
    const [loading, setLoading] = useState(false);
    
    // Dialog state
    const [open, setOpen] = useState(false);
    const [editingGamme, setEditingGamme] = useState<Partial<Gamme> | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [gammeToDelete, setGammeToDelete] = useState<number | null>(null);

    const loadGammes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await gammeApi.getAll();
            setGammes(response.data || []);
        } catch (err) {
            console.error("Error loading gammes", err);
            blobticMessage.error("Error", t('common.error.loading') || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [blobticMessage.error, t]);

    useEffect(() => {
        loadGammes();
    }, [loadGammes]);

    const handleOpen = (gamme?: Gamme) => {
        setEditingGamme(gamme || { code: '', name: '', description: '' });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingGamme(null);
    };

    const handleSave = async () => {
        if (!editingGamme || !editingGamme.code || !editingGamme.name) {
            blobticMessage.error("Validation", t('maker.msg.error.required') || "Required fields missing");
            return;
        }

        try {
            if (editingGamme.id) {
                const result = await gammeApi.update(editingGamme.id, editingGamme);
                blobticMessage.success("Success", result.message || t('common.messages.saved'));
            } else {
                const result = await gammeApi.create(editingGamme);
                blobticMessage.success("Success", result.message || t('common.messages.created'));
            }
            handleClose();
            loadGammes();
        } catch (err: any) {
            console.error("Error saving gamme", err);
            blobticMessage.error("Error", err.message || t('common.messages.save_error'));
        }
    };

    const handleDeleteClick = (id: number) => {
        setGammeToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (gammeToDelete) {
            try {
                const result = await gammeApi.delete(gammeToDelete);
                if (result.success) {
                    blobticMessage.success("Success", result.message || t('common.messages.deleted_success'));
                    loadGammes();
                } else {
                    blobticMessage.error("Error", result.message || t('common.messages.delete_error'));
                }
            } catch (err: any) {
                console.error("Error deleting gamme", err);
                blobticMessage.error("Error", err.message || t('common.messages.delete_error'));
            } finally {
                setDeleteConfirmOpen(false);
                setGammeToDelete(null);
            }
        }
    };

    const handleChange = (field: keyof Gamme, value: any) => {
        setEditingGamme(prev => prev ? { ...prev, [field]: value } : null);
    };

    return (
        <GenericPage
            title={t('maker.gamme.title') || "Gammes"}
            icon={<LibraryBooks />}
            additionalActions={
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                    {t('common.actions.new') || "New"}
                </Button>
            }
        >
            <Box sx={{ p: 3 }}>
                <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell>{t('maker.field.code') || "Code"}</TableCell>
                                <TableCell>{t('maker.field.name') || "Name"}</TableCell>
                                <TableCell>{t('maker.field.description') || "Description"}</TableCell>
                                <TableCell align="right">{t('common.actions.actions') || "Actions"}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : gammes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        {t('zoom.empty.data') || "No data available"}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                gammes.map((gamme) => (
                                    <TableRow key={gamme.id} hover>
                                        <TableCell sx={{ fontWeight: 'medium' }}>{gamme.code}</TableCell>
                                        <TableCell>{gamme.name}</TableCell>
                                        <TableCell>{gamme.description || '-'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleOpen(gamme)} color="primary">
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDeleteClick(gamme.id)} color="error">
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Edit Dialog */}
            <RootDialogPage
                open={open}
                onClose={handleClose}
                title={editingGamme?.id ? (t('common.actions.edit') || "Edit Gamme") : (t('common.actions.new') || "New Gamme")}
                onAction={handleSave}
                actionLabel={t('common.actions.save') || "Save"}
                maxWidth="sm"
                fullWidth
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField
                        label={t('maker.field.code') || "Code"}
                        fullWidth
                        value={editingGamme?.code || ''}
                        onChange={(e) => handleChange('code', e.target.value)}
                        required
                    />
                    <TextField
                        label={t('maker.field.name') || "Name"}
                        fullWidth
                        value={editingGamme?.name || ''}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                    />
                    <TextField
                        label={t('maker.field.description') || "Description"}
                        fullWidth
                        multiline
                        rows={3}
                        value={editingGamme?.description || ''}
                        onChange={(e) => handleChange('description', e.target.value)}
                    />
                </Box>
            </RootDialogPage>

            {/* Delete Confirmation */}
            <RootDialogPage
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                title={t('common.confirm.title.delete') || "Confirm Deletion"}
                onAction={handleDeleteConfirm}
                actionLabel={t('common.actions.delete') || "Delete"}
            >
                <Typography>
                    {t('common.confirm.delete') || "Are you sure you want to delete this item?"}
                </Typography>
            </RootDialogPage>
        </GenericPage>
    );
};

export default GammePage;
